import { logger } from '@magicschool/logger';
import { PostgrestError, type PostgrestSingleResponse } from '@supabase/supabase-js';

export const MULTIPLE_ROWS_ON_SINGLE_ERROR = {
  success: false as const,
  error: new PostgrestError({
    code: 'PGRST116',
    details: 'Results contain multiple rows, using "single()" requires 1 row',
    hint: '',
    message: 'JSON object requested, multiple (or no) rows returned',
  }),
  data: null,
  count: null,
  status: 406,
  statusText: 'Not Acceptable',
};

/**
 * Fabricates the 0-rows PostgREST error shape so a caller-detected not-found (e.g.
 * `.maybeSingle()` → null) hits the same `handleError` 404 mapping as a real `.single()` not-found.
 */
export const zeroRowsError = (message: string) => ({
  success: false as const,
  error: new PostgrestError({
    code: 'PGRST116',
    details: 'Results contain 0 rows',
    hint: '',
    message,
  }),
  data: null,
  count: null,
  status: 406,
  statusText: 'Not Acceptable',
});

export type SupabaseErrorType<T> = Extract<
  PostgrestSingleResponse<T>,
  {
    error: {
      message: string;
      details: string;
      hint: string;
      code: string;
    };
  }
>;

export class SupabaseError<T> extends Error {
  supabaseError: SupabaseErrorType<T>;
  constructor(response: SupabaseErrorType<T>) {
    super(response.error.message);
    this.supabaseError = response;
    const stack = new Error().stack;
    // warn, not error: the constructor cannot know whether the caller handles this, and many do.
    // Kept (not removed) because server actions swallow errors silently; this is their only trace.
    logger.warn('SupabaseError', response, stack);
  }
}

export class NoUserFoundError extends Error {
  constructor(message = 'No user found in session.') {
    super(message);
    this.name = 'NoUserFoundError';
  }
}

export const isNoUserFoundError = (error: unknown): error is NoUserFoundError => error instanceof NoUserFoundError;
